import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames';

//assets
import LoadsmartLogo from '@/public/images/logos/loadsmart_logotype.svg';

//styles
import {
  ContainerWrapper,
  MainContent,
  LogoWrapper,
  NavWrapper,
  NavContent,
  MobileMenuTrigger,
  MobileMenuAnimator,
  MobileMenuLabel,
  MenuItem,
  MenuButton,
  ItemLabel,
  Carret,
  SubmenuList,
  SubmenuLink,
  SubmenuLinkLabel,
  SubmenuLinkDesc,
} from './Navigation.styles';

const menuItems = [
  {
    label: 'Platform',
    highlight: '/platform',
    cta: null,
    submenu: [
      {
        label: 'Loadsmart Platform',
        description: `One connected platform — the AI-native TMS and the industry's leading dock scheduling, gate and yard network.`,
        url: '/',
        external: false,
      },
      {
        label: 'Loadsmart AI',
        description: `The autonomous transportation execution platform.`,
        url: '/ai/',
        external: false,
      },
      {
        label: 'Transportation Management System',
        description: `Plan, procure and execute freight with the next generation Transportation Management System.`,
        url: 'https://shipperguide.com/?utm_source=loadsmart-website',
        external: true,
      },
      {
        label: <>Dock, Gate &amp; Yard</>,
        description: `Schedule, track, and measure performance for every dock appointment.`,
        url: `${process.env.OPENDOCK_URL}/`,
        external: true,
      },
    ],
  },
  {
    label: 'AI',
    highlight: '/ai',
    cta: null,
    size: 'small',
    submenu: [
      {
        label: 'Loadsmart AI',
        url: '/ai/',
        external: false,
      },
      {
        label: 'Document Collection & Filing',
        url: '/ai/document-collection-and-filing/',
        external: false,
      },
      {
        label: 'Tracking & Status Updates',
        url: '/ai/tracking-and-status-updates/',
        external: false,
      },
      {
        label: 'Tender Failures',
        url: '/ai/tender-failures/',
        external: false,
      },
      {
        label: 'Proactive Load Audits',
        url: '/ai/proactive-load-audits/',
        external: false,
      },
      {
        label: 'Claims Processing',
        url: '/ai/claims-processing/',
        external: false,
      },
      {
        label: 'Scheduling & Re-Scheduling',
        url: '/ai/scheduling-and-re-scheduling/',
        external: false,
      },
    ],
  },
  {
    label: 'Freight Services',
    highlight: '/shipper/',
    cta: null,
    submenu: [
      {
        label: 'Freight Brokerage',
        description: `Instantly price, book, and ship with guaranteed capacity for FTL, LTL, PTL, Expedited, Drayage, and Multimodal.`,
        url: '/shipper/',
        external: false,
      },
      {
        label: 'Managed transportation',
        description: `Consulting services, end-to-end freight planning,
        procurement and execution services.`,
        url: '/shipper/managed-transportation/',
        external: false,
      },
      {
        label: 'Flatbed messenger',
        description: `Get access to premium capacity from top performing carriers.`,
        url: '/shipper/flatbed-messenger/',
        external: false,
      },
      {
        label: 'Expedited shipping',
        description: `Get time-critical freight there faster.`,
        url: '/shipper/expedited-shipping/',
        external: false,
      },
    ],
  },
  {
    label: 'About',
    highlight: '/about',
    cta: null,
    size: 'small',
    submenu: [
      {
        label: 'Who we are',
        url: '/about/',
        external: false,
      },
      {
        label: 'Careers',
        url: '/careers/',
        external: false,
      },
      {
        label: 'Partnerships',
        url: 'https://lp.loadsmart.com/loadsmart-partner-portal-1',
        external: true,
      },
      {
        label: 'Resources',
        url: 'https://content.loadsmart.com/',
        external: true,
      },
      {
        label: 'Blog',
        url: 'https://blog.loadsmart.com/',
        external: true,
      },
      {
        label: 'Community',
        url: 'https://community.loadsmart.com/hc/en-us',
        external: true,
      },
      {
        label: 'Loadsmart API',
        url: 'https://developer.loadsmart.com/docs/',
        external: true,
      },
    ],
  },
  {
    label: 'Contact',
    highlight: '/contact',
    cta: { url: '/contact/', external: false },
    submenu: [],
  },
  {
    label: 'Login',
    highlight: '/login',
    cta: null,
    size: 'small',
    submenu: [
      {
        label: 'Transportation Management System',
        url: `${process.env.SHIPPERGUIDE_URL}/login?utm_medium=web_site&utm_campaign=migrationsg`,
        external: true,
      },
      {
        label: <>Dock, Gate &amp; Yard</>,
        url: 'https://nova.opendock.com/login',
        external: true,
      },
      {
        label: 'Carriers',
        url: `${process.env.CARRIER_URL}/login`,
        external: true,
      },
      {
        label: 'All Products',
        url: '/login/',
        external: false,
      },
    ],
  },
  {
    label: 'Signup',
    highlight: '/signup',
    cta: null,
    size: 'small',
    submenu: [
      {
        label: 'Shippers',
        url: `${process.env.SHIPPERGUIDE_URL}/signup`,
        external: true,
      },
      {
        label: 'Carriers',
        url: `${process.env.CARRIER_URL}/signup`,
        external: true,
      },
      {
        label: 'All Products',
        url: '/login/',
        external: false,
      },
    ],
  },
];

export function Navigation({ theme }) {
  const router = useRouter();
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuActiveIndex, setMenuActiveIndex] = useState(-1);

  const closeDropdownWhenClickOutside = () => {
    document.addEventListener('click', function (event) {
      const isClickInsideElement = document
        .getElementById('main-menu-wrapper')
        .contains(event.target);
      if (!isClickInsideElement) {
        setMenuOpen(false);
        setMenuActiveIndex(-1);
      }
    });
  };

  useEffect(() => {
    if (menuOpen || menuActiveIndex >= 0) {
      closeDropdownWhenClickOutside();
    }
  }, [menuOpen, menuActiveIndex]);

  return (
    <ContainerWrapper theme={theme}>
      <MainContent id="main-menu-wrapper">
        <LogoWrapper as="a" href="/" aria-label="Home">
          <LoadsmartLogo />
        </LogoWrapper>

        <NavWrapper className={menuOpen ? `open` : ``}>
          <NavContent>
            {menuItems.map((item, index) => {
              const keyNav = `navmenu-${item.label}-${index}`;

              var menuListClasses = classNames({
                open: menuActiveIndex === index,
                small: item?.size,
                highlight: router.pathname.includes(item.highlight),
              });

              return (
                <MenuItem key={keyNav}>
                  <MenuButton
                    as={item.submenu.length <= 0 ? 'a' : 'button'}
                    {...(item.cta?.url && { href: item.cta.url })}
                    {...(item.cta?.external && { target: '_blank' })}
                    {...(item.cta?.external && { rel: 'noreferrer noopener' })}
                    theme={theme}
                    onClick={() =>
                      setMenuActiveIndex(menuActiveIndex === index ? -1 : index)
                    }
                    className={menuListClasses}
                  >
                    <ItemLabel>{item.label}</ItemLabel>
                    {!item.submenu.length <= 0 && (
                      <Carret width="16" height="16" theme={theme} />
                    )}
                  </MenuButton>
                  {!item.submenu.length <= 0 && (
                    <SubmenuList className={menuListClasses}>
                      {item.submenu.map((subitem, subindex) => {
                        const keySubNav = `navsubmenu-${item.label}-${subindex}`;

                        var submenuLinkClasses = classNames({
                          bold: !subitem.description,
                          highlight: router.pathname === subitem.url,
                        });

                        return (
                          <SubmenuLink
                            key={keySubNav}
                            href={subitem.url}
                            {...(subitem?.external && { target: '_blank' })}
                            {...(subitem?.external && {
                              rel: 'noreferrer noopener',
                            })}
                            className={submenuLinkClasses}
                          >
                            <SubmenuLinkLabel className={submenuLinkClasses}>
                              {subitem.label}
                            </SubmenuLinkLabel>
                            {subitem.description && (
                              <SubmenuLinkDesc>
                                {subitem.description}
                              </SubmenuLinkDesc>
                            )}
                          </SubmenuLink>
                        );
                      })}
                    </SubmenuList>
                  )}
                </MenuItem>
              );
            })}
          </NavContent>
        </NavWrapper>

        <MobileMenuTrigger
          onClick={() => setMenuOpen(!menuOpen)}
          className={menuOpen ? `open` : ``}
          aria-labelledby="labeldiv"
        >
          <MobileMenuLabel id="labeldiv">Open Menu</MobileMenuLabel>
          <MobileMenuAnimator
            theme={theme}
            className={menuOpen ? `open` : ``}
            aria-hidden="true"
          >
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </MobileMenuAnimator>
        </MobileMenuTrigger>
      </MainContent>
    </ContainerWrapper>
  );
}
